<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import format from 'date-fns/format';
import isSameMonth from 'date-fns/isSameMonth';
import isToday from 'date-fns/isToday';
// TODO locale dinámico: leer el locale activo de useI18n() y mapear al objeto
// date-fns correspondiente (es/en/etc.). Por ahora se usa 'es' fijo. (P3)
import { es } from 'date-fns/locale';
import Button from 'dashboard/components-next/button/Button.vue';
import { monthMatrix, taskDotClass } from '../../leads/helper';

const props = defineProps({
  monthCursor: { type: Date, required: true },
  tasks: { type: Array, default: () => [] },
});
const emit = defineEmits(['prev', 'next', 'today', 'edit']);

const { t } = useI18n();

const MAX_CHIPS = 3;
const weeks = computed(() => monthMatrix(props.monthCursor));
const weekdayLabels = computed(() =>
  weeks.value[0].map(day => format(day, 'EEE', { locale: es }))
);
const monthLabel = computed(() =>
  format(props.monthCursor, 'LLLL yyyy', { locale: es })
);

// Mapa 'yyyy-MM-dd' → tareas, calculado una sola vez por cambio de tasks.
const tasksByDay = computed(() => {
  const map = {};
  props.tasks.forEach(task => {
    if (!task.due_at) return;
    const key = format(new Date(task.due_at), 'yyyy-MM-dd');
    (map[key] ||= []).push(task);
  });
  return map;
});
const chipsForDay = day => tasksByDay.value[format(day, 'yyyy-MM-dd')] || [];
</script>

<template>
  <div class="flex flex-col gap-3">
    <header class="flex items-center justify-between">
      <span class="text-base font-semibold capitalize text-n-slate-12">{{
        monthLabel
      }}</span>
      <div class="flex items-center gap-1">
        <Button
          ghost
          slate
          sm
          icon="i-lucide-chevron-left"
          @click="emit('prev')"
        />
        <Button
          faded
          slate
          sm
          :label="t('LEADS.AGENDA.TODAY_BTN')"
          @click="emit('today')"
        />
        <Button
          ghost
          slate
          sm
          icon="i-lucide-chevron-right"
          @click="emit('next')"
        />
      </div>
    </header>

    <!-- overflow-x-auto: en pantallas <400 px el grid de 7 col scrollea horizontal -->
    <div class="overflow-x-auto">
      <div
        class="grid grid-cols-7 gap-px text-xs font-medium text-center text-n-slate-10 min-w-[21rem]"
      >
        <span
          v-for="(label, i) in weekdayLabels"
          :key="i"
          class="py-1 capitalize"
          >{{ label }}</span
        >
      </div>

      <div
        class="grid grid-cols-7 gap-px overflow-hidden rounded-lg bg-n-weak min-w-[21rem]"
      >
        <template v-for="(week, wi) in weeks" :key="wi">
          <div
            v-for="day in week"
            :key="day.toISOString()"
            class="min-h-[5.5rem] p-1.5 bg-n-background flex flex-col gap-1"
            :class="{ 'opacity-40': !isSameMonth(day, monthCursor) }"
          >
            <span
              class="text-xs"
              :class="
                isToday(day) ? 'font-bold text-n-brand' : 'text-n-slate-10'
              "
            >
              {{ format(day, 'd') }}
            </span>
            <button
              v-for="task in chipsForDay(day).slice(0, MAX_CHIPS)"
              :key="task.id"
              type="button"
              class="flex items-center gap-1 px-1 py-0.5 text-xs rounded text-left truncate hover:bg-n-alpha-2"
              @click="emit('edit', task)"
            >
              <span
                class="size-1.5 rounded-full shrink-0"
                :class="taskDotClass(task.due_at)"
              />
              <span class="truncate text-n-slate-12">{{ task.title }}</span>
            </button>
            <span
              v-if="chipsForDay(day).length > MAX_CHIPS"
              class="px-1 text-xs text-n-slate-9"
            >
              {{
                t('LEADS.AGENDA.MORE', {
                  count: chipsForDay(day).length - MAX_CHIPS,
                })
              }}
            </span>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
